/* eslint-disable @typescript-eslint/no-magic-numbers */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { catchError, delay, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import { AlertingService } from '@core/api/alerting/alerting.service';
import { MultiSiteDashboardService } from '@core/api/multi-site-dashboard/multi-site-dashboard.service';
import { ToastService } from '@core/services/toast.service';
import { activeUserChanged } from '@core/stores/auth/auth.actions';
import { synchroniseTenantsCache } from '@core/stores/tenants/tenants.actions';
import { selectTenants } from '@core/stores/tenants/tenants.selectors';
import { reconnectToWebsocketIsSafe, websocketFailure } from '@core/stores/websocket/websocket.actions';

import { mapAlertsRequestStoreModelToApiModel, mapAlertsResponseApiModelToStoreModel } from '@domain/alerting/alerting-models.mappings';
import { AlertStateStoreModelEnum } from '@domain/alerting/alerting.enums';
import {
  mapDashboardOverviewApiModelToStoreModel,
  mapDashboardOverviewWebsocketMessageApiModelToStoreModel,
  mapDashboardStatsApiModelToStoreModel,
  mapEnergyCostApiModelToStoreModel,
  mapVehicleStateOfChargeSummaryApiModelToStoreModel,
} from '@domain/multi-site-dashboard/multi-site-dashboard-models.mappings';
import { TenantDtoStoreModel } from '@domain/tenant/tenant.models';

import { ToastMessageType } from '@shared/components/toast-message/toast-message.view-model';
import { ACPredicate, cancelOnAction } from '@shared/operators/cancel-on-action.operator';
import { isDefined } from '@shared/utils/is-defined';
import { StoreToStorageSync } from '@shared/utils/store-to-storage-sync';

import { reconcileSelectedTenantId } from '../helpers/reconcile-selected-tenant-id';
import { buildEnergyCostDateRange } from './helpers/build-energy-cost-date-range';
import { buildVehicleSocSummaryDateRange } from './helpers/build-vehicle-soc-summary-date-range';
import {
  clearEnergyCost,
  clearMultiSiteDashboardData,
  clearOverview,
  clearSiteAlerts,
  clearStats,
  clearVehicleStateOfChargeSummary,
  loadEnergyCost,
  loadEnergyCostFailure,
  loadEnergyCostSuccess,
  loadOverview,
  loadOverviewFailure,
  loadOverviewSuccess,
  loadSiteAlerts,
  loadSiteAlertsFailure,
  loadSiteAlertsSuccess,
  loadStats,
  loadStatsFailure,
  loadStatsSuccess,
  loadVehicleStateOfChargeSummary,
  loadVehicleStateOfChargeSummaryFailure,
  loadVehicleStateOfChargeSummarySuccess,
  overviewSocketMessageArrive,
  reconcileSelectedTenant,
  reconcileSelectedTenantAfterActiveUserChange,
  selectTenant,
  setCardsLayout,
  setEnergyCostRange,
  setReconciledSelectedTenant,
  setVehicleSocSummaryRange,
} from './multi-site-dashboard.actions';
import {
  selectCanViewFinanceAnalytics,
  selectEnergyCostRange,
  selectIsSelectedTenantVehicleStateOfChargeSummaryEnabled,
  selectSelectedTenantId,
  selectVehicleSocSummaryRange,
} from './multi-site-dashboard.selectors';

@Injectable()
export class MultiSiteDashboardEffects {
  readonly #actions$ = inject(Actions);
  readonly #store = inject(Store);
  readonly #multiSiteDashboardService = inject(MultiSiteDashboardService);
  readonly #alertingService = inject(AlertingService);
  readonly #toastService = inject(ToastService);

  public reconcileSelectedTenant$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(reconcileSelectedTenant, reconcileSelectedTenantAfterActiveUserChange),
      concatLatestFrom(() => combineLatest([this.#store.select(selectSelectedTenantId), this.#store.select(selectTenants)])),
      filter(
        (
          value
        ): value is [
          ReturnType<typeof reconcileSelectedTenant> | ReturnType<typeof reconcileSelectedTenantAfterActiveUserChange>,
          [string | undefined, TenantDtoStoreModel[]],
        ] => isDefined(value[1][1])
      ),
      map(([action, [selectedTenantId, tenants]]) => {
        const tenantId = reconcileSelectedTenantId(tenants, selectedTenantId, StoreToStorageSync.getMultiSiteDashboardSelectedTenantId());
        return setReconciledSelectedTenant({
          tenantId,
          reloadData: action.type === reconcileSelectedTenantAfterActiveUserChange.type && tenantId === selectedTenantId,
        });
      })
    )
  );

  public reconcileSelectedTenantAfterActiveUserChange$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(activeUserChanged),
      switchMap(() =>
        this.#actions$.pipe(
          ofType(synchroniseTenantsCache),
          filter(({ tenants }) => isDefined(tenants)),
          take(1),
          map(() => reconcileSelectedTenantAfterActiveUserChange())
        )
      )
    )
  );

  public reloadDataAfterActiveUserChange$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(setReconciledSelectedTenant),
      filter(({ reloadData }) => reloadData === true),
      concatLatestFrom(() =>
        combineLatest([
          this.#store.select(selectCanViewFinanceAnalytics),
          this.#store.select(selectIsSelectedTenantVehicleStateOfChargeSummaryEnabled),
          this.#store.select(selectEnergyCostRange),
          this.#store.select(selectVehicleSocSummaryRange),
        ])
      ),
      mergeMap(([, [canViewFinanceAnalytics, isVehicleStateOfChargeSummaryEnabled, energyCostRange, vehicleSocSummaryRange]]) => {
        const actions: Action[] = [clearMultiSiteDashboardData(), loadOverview(), loadStats(), loadSiteAlerts({})];
        if (canViewFinanceAnalytics) {
          actions.push(loadEnergyCost({ range: energyCostRange }));
        }
        if (isVehicleStateOfChargeSummaryEnabled) {
          actions.push(loadVehicleStateOfChargeSummary({ range: vehicleSocSummaryRange }));
        }
        return actions;
      })
    )
  );

  public reconcileSelectedTenantOnTenantCacheChange$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(synchroniseTenantsCache),
      filter(({ tenants }) => isDefined(tenants)),
      map(() => reconcileSelectedTenant())
    )
  );

  // Persist after the reducer has validated the saved selection against the current user's tenants.
  public persistSelectedTenant$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(setReconciledSelectedTenant, selectTenant),
        concatLatestFrom(() => this.#store.select(selectSelectedTenantId)),
        tap(([, tenantId]) => {
          if (tenantId) {
            StoreToStorageSync.setMultiSiteDashboardSelectedTenantId(tenantId);
          }
        })
      ),
    { dispatch: false }
  );

  public loadOverview$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadOverview),
      concatLatestFrom(() => this.#store.select(selectSelectedTenantId).pipe(filter((tenantId): tenantId is string => !!tenantId))),
      switchMap(([, tenantId]) =>
        this.#multiSiteDashboardService.getOverview$(tenantId).pipe(
          map((response) => loadOverviewSuccess({ overview: mapDashboardOverviewApiModelToStoreModel(response) })),
          catchError((error: HttpErrorResponse) => of(loadOverviewFailure({ error: error.message })))
        )
      ),
      cancelOnAction(this.#actions$, [clearOverview, clearMultiSiteDashboardData])
    )
  );

  public loadStats$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadStats),
      concatLatestFrom(() => this.#store.select(selectSelectedTenantId).pipe(filter((tenantId): tenantId is string => !!tenantId))),
      switchMap(([, tenantId]) =>
        this.#multiSiteDashboardService.getStats$(tenantId).pipe(
          map((response) => loadStatsSuccess({ stats: mapDashboardStatsApiModelToStoreModel(response) })),
          catchError((error: HttpErrorResponse) => of(loadStatsFailure({ error: error.message })))
        )
      ),
      cancelOnAction(this.#actions$, [clearStats, clearMultiSiteDashboardData])
    )
  );

  public loadSiteAlerts$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadSiteAlerts),
      concatLatestFrom(() => this.#store.select(selectSelectedTenantId).pipe(filter((tenantId): tenantId is string => !!tenantId))),
      switchMap(([{ polling }, tenantId]) =>
        this.#alertingService
          .getAlerts$(mapAlertsRequestStoreModelToApiModel({ tenantIds: [tenantId], states: [AlertStateStoreModelEnum.Firing] }))
          .pipe(
            map((response) => loadSiteAlertsSuccess({ alerts: mapAlertsResponseApiModelToStoreModel(response).alerts, polling })),
            catchError((error: HttpErrorResponse) => of(loadSiteAlertsFailure({ error: error.message })))
          )
      ),
      cancelOnAction(this.#actions$, [clearSiteAlerts, clearMultiSiteDashboardData])
    )
  );

  // Poll the selected tenant's alerts every 5s, mirroring the alerting page. Cancelled by a fresh
  // (non-polling) load on tenant change and by the screen-leave/tenant-change clear.
  public startPollingSiteAlerts$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadSiteAlertsSuccess),
      delay(5000),
      map(() => loadSiteAlerts({ polling: true })),
      cancelOnAction(this.#actions$, [
        {
          action: loadSiteAlerts,
          predicate: ({ polling }) => !polling,
        } as ACPredicate<typeof loadSiteAlerts>,
        clearSiteAlerts,
        clearMultiSiteDashboardData,
      ])
    )
  );

  public loadEnergyCost$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadEnergyCost),
      concatLatestFrom(() => this.#store.select(selectCanViewFinanceAnalytics)),
      filter(([, canViewFinanceAnalytics]) => canViewFinanceAnalytics),
      concatLatestFrom(() => this.#store.select(selectSelectedTenantId).pipe(filter((tenantId): tenantId is string => !!tenantId))),
      switchMap(([[{ range }], tenantId]) =>
        this.#multiSiteDashboardService.getEnergyCost$(tenantId, buildEnergyCostDateRange(range)).pipe(
          map((response) => loadEnergyCostSuccess({ energyCost: mapEnergyCostApiModelToStoreModel(response) })),
          catchError((error: HttpErrorResponse) => of(loadEnergyCostFailure({ error: error.message })))
        )
      ),
      cancelOnAction(this.#actions$, [clearEnergyCost, clearMultiSiteDashboardData])
    )
  );

  // The energy-cost endpoint has no websocket topic, so the selected range is the only thing that
  // needs persisting; the load itself is dispatched alongside setEnergyCostRange by the page service.
  public persistEnergyCostRange$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(setEnergyCostRange),
        tap(({ range }) => StoreToStorageSync.setMultiSiteDashboardEnergyCostRange(range))
      ),
    { dispatch: false }
  );

  public loadVehicleStateOfChargeSummary$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadVehicleStateOfChargeSummary),
      concatLatestFrom(() => this.#store.select(selectIsSelectedTenantVehicleStateOfChargeSummaryEnabled)),
      filter(([, isVehicleStateOfChargeSummaryEnabled]) => isVehicleStateOfChargeSummaryEnabled === true),
      concatLatestFrom(() => this.#store.select(selectSelectedTenantId).pipe(filter((tenantId): tenantId is string => !!tenantId))),
      switchMap(([[{ range }], tenantId]) =>
        this.#multiSiteDashboardService.getVehicleStateOfChargeSummary$(tenantId, buildVehicleSocSummaryDateRange(range)).pipe(
          map((response) =>
            loadVehicleStateOfChargeSummarySuccess({ summary: mapVehicleStateOfChargeSummaryApiModelToStoreModel(response) })
          ),
          catchError((error: HttpErrorResponse) => of(loadVehicleStateOfChargeSummaryFailure({ error: error.message })))
        )
      ),
      cancelOnAction(this.#actions$, [clearVehicleStateOfChargeSummary, clearMultiSiteDashboardData])
    )
  );

  // Mirrors persistEnergyCostRange$: the summary endpoint has no websocket topic, so the selected
  // range is persisted on change; the reload is dispatched alongside setVehicleSocSummaryRange by the
  // page service.
  public persistVehicleSocSummaryRange$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(setVehicleSocSummaryRange),
        tap(({ range }) => StoreToStorageSync.setMultiSiteDashboardVehicleSocSummaryRange(range))
      ),
    { dispatch: false }
  );

  public reloadOverviewOnWebsocketReconnect$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadOverview),
      switchMap(() => this.#actions$.pipe(ofType(reconnectToWebsocketIsSafe))),
      map(() => loadOverview()),
      cancelOnAction(this.#actions$, [clearOverview, clearMultiSiteDashboardData])
    )
  );

  public startObservingOverviewWebsocketMessages$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadOverviewSuccess),
      concatLatestFrom(() => this.#store.select(selectSelectedTenantId).pipe(filter((tenantId): tenantId is string => !!tenantId))),
      switchMap(([, tenantId]) =>
        this.#multiSiteDashboardService
          .getOverviewWebsocketMessage$(tenantId)
          .pipe(
            map((message) => overviewSocketMessageArrive({ overview: mapDashboardOverviewWebsocketMessageApiModelToStoreModel(message) }))
          )
      ),
      cancelOnAction(this.#actions$, [clearOverview, clearMultiSiteDashboardData, websocketFailure])
    )
  );

  public syncCardsLayoutWithStorage$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(setCardsLayout),
        tap(({ layout }) => StoreToStorageSync.setStorageData({ multiSiteDashboardCardsLayout: layout }))
      ),
    { dispatch: false }
  );

  public showErrorToastOnFailures$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(loadOverviewFailure, loadStatsFailure, loadSiteAlertsFailure, loadEnergyCostFailure, loadVehicleStateOfChargeSummaryFailure),
        tap(({ error }) => {
          this.#toastService.openToast({
            type: ToastMessageType.Error,
            message$: of(error),
            closeable: true,
            duration: 5000,
          });
        }),
        cancelOnAction(this.#actions$, clearMultiSiteDashboardData)
      ),
    { dispatch: false }
  );
}
